/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";

// @mui material hooks
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// @mui material components
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";

import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Autocomplete from "@mui/material/Autocomplete";

// Icons
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE } from "utils";

function ApelDinas() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dataApelDinas, setDataApelDinas] = useState(null);
  const [selectedTanggal, setSelectedTanggal] = useState("");
  const [userId, setUserId] = useState(null);
  const [userDinasId, setUserDinasId] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const location = useLocation();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // State untuk modal apel
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPegawai, setSelectedPegawai] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editApelId, setEditApelId] = useState(null);
  const [form, setForm] = useState({
    uid_RSS: "",
    tanggal: "",
    jenis_apel: "Pagi",
    jam_absen: "",
    status: "Hadir",
    keterangan: "",
  });

  // State untuk modal detail apel
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedApelDetail, setSelectedApelDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // State untuk switch statistik apel
  const [selectedApelType, setSelectedApelType] = useState("pagi");

  const statusOptions = [
    "Hadir",
    "Izin",
    "Sakit",
    "Dinas Luar",
    "Tanpa Berita",
    "Cuti",
    "Tugas Belajar",
  ];
  const jenisApelOptions = ["Pagi", "Siang", "Lainnya"];

  // Fungsi untuk mendapatkan jam saat ini dengan WITA (UTC+8)
  const getCurrentTimeWITA = () => {
    const now = new Date();
    const witaOffset = 8 * 60; // UTC+8 dalam menit
    const witaTime = new Date(now.getTime() + witaOffset * 60 * 1000);
    return witaTime.toTimeString().slice(0, 5); // Format HH:MM
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setUserId(user.uid_RSS || user.uid);
    // Allow dinas_id override via query param
    const params = new URLSearchParams(location.search);
    const qDinasId = params.get("dinas_id");
    const qTanggal = params.get("tanggal");
    const qNama = params.get("nama");
    setUserDinasId(qDinasId ? Number(qDinasId) : user.dinas_id);
    setUserStatus(user.status || user.role);

    // Set default tanggal ke hari ini dengan WITA (UTC+8)
    const today = new Date();
    const witaOffset = 8 * 60; // UTC+8 dalam menit
    const witaDate = new Date(today.getTime() + witaOffset * 60 * 1000);
    const todayWITA = witaDate.toISOString().split("T")[0];
    setSelectedTanggal(qTanggal || todayWITA);

    // Auto-select dinas berdasarkan user yang login
    if (user.dinas_id && !qDinasId) {
      setUserDinasId(user.dinas_id);
    }

    // Inisialisasi form dengan jam WITA saat ini
    const currentTimeWITA = getCurrentTimeWITA();
    setForm((prevForm) => ({
      ...prevForm,
      jam_absen: currentTimeWITA,
    }));
  }, [location.search]);

  const fetchApelDinas = () => {
    if (!userDinasId || !selectedTanggal) {
      swal("Error", "Data dinas atau tanggal tidak valid", "error");
      return;
    }

    setLoading(true);
    axios
      .get(
        `${API_ESA_OFFICE}apel-harian/dinas/${userDinasId}/tanggal/${selectedTanggal}`
      )
      .then((res) => {
        setDataApelDinas(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching apel dinas:", err);
        setLoading(false);
        swal("Error", "Gagal mengambil data apel dinas", "error");
      });
  };

  const handleTanggalChange = (e) => {
    setSelectedTanggal(e.target.value);
  };

  // Auto-fetch data ketika tanggal berubah
  useEffect(() => {
    if (userDinasId && selectedTanggal && !loading) {
      fetchApelDinas();
    }
  }, [selectedTanggal, userDinasId]);

  // Fungsi untuk modal apel
  const handleOpenDialog = (pegawai) => {
    setSelectedPegawai(pegawai);
    setIsEditMode(false);
    setEditApelId(null);

    // Cek apakah sudah ada data apel pagi atau siang
    const apelPagi = pegawai.apel_pagi;
    const apelSiang = pegawai.apel_siang;

    if (apelPagi && apelSiang) {
      // Jika keduanya sudah ada, tanyakan user mau edit yang mana
      swal({
        title: "Pilih Jenis Apel",
        text: "Data apel pagi dan siang sudah ada. Pilih yang ingin diedit:",
        icon: "info",
        buttons: {
          pagi: "Apel Pagi",
          siang: "Apel Siang",
          cancel: "Batal",
        },
      }).then((value) => {
        if (value === "pagi") {
          openEditDialog(pegawai, apelPagi, "Pagi");
        } else if (value === "siang") {
          openEditDialog(pegawai, apelSiang, "Siang");
        }
      });
    } else if (apelPagi) {
      // Jika hanya apel pagi yang ada, tanyakan mau edit pagi atau tambah siang
      swal({
        title: "Pilih Aksi",
        text: "Data apel pagi sudah ada. Pilih aksi:",
        icon: "info",
        buttons: {
          editPagi: "Edit Apel Pagi",
          addSiang: "Tambah Apel Siang",
          cancel: "Batal",
        },
      }).then((value) => {
        if (value === "editPagi") {
          openEditDialog(pegawai, apelPagi, "Pagi");
        } else if (value === "addSiang") {
          openAddDialog(pegawai, "Siang");
        }
      });
    } else if (apelSiang) {
      // Jika hanya apel siang yang ada, tanyakan mau edit siang atau tambah pagi
      swal({
        title: "Pilih Aksi",
        text: "Data apel siang sudah ada. Pilih aksi:",
        icon: "info",
        buttons: {
          editSiang: "Edit Apel Siang",
          addPagi: "Tambah Apel Pagi",
          cancel: "Batal",
        },
      }).then((value) => {
        if (value === "editSiang") {
          openEditDialog(pegawai, apelSiang, "Siang");
        } else if (value === "addPagi") {
          openAddDialog(pegawai, "Pagi");
        }
      });
    } else {
      // Jika belum ada data sama sekali, tanyakan mau tambah yang mana
      swal({
        title: "Pilih Jenis Apel",
        text: "Pilih jenis apel yang ingin ditambahkan:",
        icon: "info",
        buttons: {
          pagi: "Apel Pagi",
          siang: "Apel Siang",
          cancel: "Batal",
        },
      }).then((value) => {
        if (value === "pagi" || value === "siang") {
          openAddDialog(pegawai, value === "pagi" ? "Pagi" : "Siang");
        }
      });
    }
  };

  const openAddDialog = (pegawai, jenisApel) => {
    const currentTimeWITA = getCurrentTimeWITA();

    setSelectedPegawai(pegawai);
    setIsEditMode(false);
    setEditApelId(null);
    setForm({
      uid_RSS: pegawai.uid_RSS.toString(),
      tanggal: selectedTanggal,
      jenis_apel: jenisApel,
      jam_absen: currentTimeWITA, // Default jam saat ini dengan WITA
      status: "Hadir",
      keterangan: "",
    });
    setOpenDialog(true);
  };

  const openEditDialog = (pegawai, apelData, jenisApel) => {
    setSelectedPegawai(pegawai);
    setIsEditMode(true);
    setEditApelId(apelData.id_apel);
    setForm({
      uid_RSS: pegawai.uid_RSS.toString(),
      tanggal: selectedTanggal,
      jenis_apel: jenisApel,
      jam_absen: apelData.jam_absen || "",
      status: apelData.status || "Hadir",
      keterangan: apelData.keterangan || "",
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    const currentTimeWITA = getCurrentTimeWITA();
    setOpenDialog(false);
    setSelectedPegawai(null);
    setIsEditMode(false);
    setEditApelId(null);
    setForm({
      uid_RSS: "",
      tanggal: "",
      jenis_apel: "Pagi",
      jam_absen: currentTimeWITA, // Default jam saat ini dengan WITA
      status: "Hadir",
      keterangan: "",
    });
  };

  const handleSubmit = () => {
    // Validasi form
    if (!form.tanggal || !form.jenis_apel || !form.status) {
      swal("Error", "Tanggal, jenis apel, dan status harus diisi", "error");
      return;
    }

    const submitData = {
      uid_RSS: form.uid_RSS.toString(),
      tanggal: form.tanggal,
      jenis_apel: form.jenis_apel,
      jam_absen: form.jam_absen || null,
      status: form.status,
      keterangan: form.keterangan || null,
    };

    if (isEditMode && editApelId) {
      // Update existing apel
      axios
        .put(`${API_ESA_OFFICE}apel-harian/${editApelId}`, submitData)
        .then((res) => {
          swal("Success", "Data apel harian berhasil diperbarui", "success");
          handleCloseDialog();
          fetchApelDinas(); // Refresh data
        })
        .catch((err) => {
          console.error("Error updating apel harian:", err);
          swal("Error", "Gagal memperbarui data apel harian", "error");
        });
    } else {
      // Create new apel
      axios
        .post(`${API_ESA_OFFICE}apel-harian`, submitData)
        .then((res) => {
          swal("Success", "Data apel harian berhasil dibuat", "success");
          handleCloseDialog();
          fetchApelDinas(); // Refresh data
        })
        .catch((err) => {
          console.error("Error creating apel harian:", err);
          swal("Error", "Gagal membuat data apel harian", "error");
        });
    }
  };

  // Fungsi untuk modal detail apel
  const handleOpenDetailModal = (pegawai) => {
    setSelectedApelDetail(pegawai);
    setOpenDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setSelectedApelDetail(null);
  };

  // Fungsi untuk export Excel
  const handleExportExcel = () => {
    if (!dataApelDinas?.data_pegawai?.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }

    // Buat HTML table untuk Excel
    const tableHeaders = [
      "No",
      "Nama Pegawai",
      "Apel Pagi",
      "Status Pagi",
      "Apel Siang",
      "Status Siang",
    ];

    let tableRows = "";
    dataApelDinas.data_pegawai.forEach((pegawai, index) => {
      tableRows += `
        <tr>
          <td>${index + 1}</td>
          <td>${pegawai.nama_pegawai || ""}</td>
          <td>${pegawai.apel_pagi?.jam_absen || "Belum Absen"}</td>
          <td>${pegawai.apel_pagi?.status || "-"}</td>
          <td>${pegawai.apel_siang?.jam_absen || "Belum Absen"}</td>
          <td>${pegawai.apel_siang?.status || "-"}</td>
        </tr>
      `;
    });

    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8">
          <title>Apel Harian Dinas - ${selectedTanggal}</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Apel Harian Dinas - ${dataApelDinas.nama_dinas} - ${new Date(
      selectedTanggal
    ).toLocaleDateString("id-ID")}</h2>
          <h3>Statistik:</h3>
          <ul>
            <li>Total Pegawai: ${dataApelDinas.total_pegawai}</li>
            <li>Tanggal: ${new Date(dataApelDinas.tanggal).toLocaleDateString(
              "id-ID"
            )}</li>
          </ul>
          <table>
            <thead>
              <tr>
                ${tableHeaders.map((header) => `<th>${header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    saveAs(blob, `apel-harian-dinas-${selectedTanggal}.xls`);
  };

  // Fungsi untuk export PDF
  const handleExportPDF = () => {
    if (!dataApelDinas?.data_pegawai?.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }

    // Buat PDF baru
    const doc = new jsPDF("landscape", "mm", "a4");

    // Header
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Laporan Apel Harian Dinas", 20, 20);

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Tanggal: ${new Date(selectedTanggal).toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })}`,
      20,
      30
    );

    doc.text(`Total Pegawai: ${dataApelDinas.data_pegawai.length}`, 20, 35);

    // Informasi aplikasi
    doc.setFontSize(10);
    doc.setFont("helvetica", "italic");
    doc.text("Dokumen ini dibuat dari aplikasi ESA Office", 20, 42);

    // Header tabel
    const tableHeaders = [
      "No",
      "Nama Pegawai",
      "Apel Pagi",
      "Status Pagi",
      "Apel Siang",
      "Status Siang",
    ];

    // Konfigurasi tabel
    const startY = 50;
    const rowHeight = 8;
    const colWidths = [12, 60, 35, 25, 35, 25];
    const startX = 20;
    let currentY = startY;

    // Fungsi untuk menggambar header tabel
    const drawTableHeader = () => {
      doc.setFillColor(41, 128, 185);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(255, 255, 255);

      let x = startX;
      tableHeaders.forEach((header, index) => {
        // Draw header cell background
        doc.rect(x, currentY, colWidths[index], rowHeight, "F");

        // Draw header text (center aligned)
        const textWidth = doc.getTextWidth(header);
        const cellCenter = x + colWidths[index] / 2;
        const textX = cellCenter - textWidth / 2;
        doc.text(header, textX, currentY + 5);

        x += colWidths[index];
      });
      currentY += rowHeight;
    };

    // Fungsi untuk menggambar baris data
    const drawTableRow = (data) => {
      doc.setFillColor(255, 255, 255);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);

      let x = startX;
      data.forEach((cell, index) => {
        // Alternating row colors
        if (Math.floor((currentY - startY - rowHeight) / rowHeight) % 2 === 1) {
          doc.setFillColor(248, 249, 250);
          doc.rect(x, currentY, colWidths[index], rowHeight, "F");
        } else {
          doc.setFillColor(255, 255, 255);
          doc.rect(x, currentY, colWidths[index], rowHeight, "F");
        }

        // Draw border
        doc.setDrawColor(200, 200, 200);
        doc.rect(x, currentY, colWidths[index], rowHeight, "S");

        // Add text
        const text = String(cell || "");
        // Limit text length based on column width
        const maxChars = Math.floor(colWidths[index] / 2.5); // Approximate chars per mm
        const displayText =
          text.length > maxChars
            ? text.substring(0, maxChars - 3) + "..."
            : text;
        doc.text(displayText, x + 1, currentY + 5);
        x += colWidths[index];
      });
      currentY += rowHeight;
    };

    // Gambar header tabel
    drawTableHeader();

    // Gambar data tabel
    dataApelDinas.data_pegawai.forEach((pegawai, index) => {
      // Check if we need a new page
      if (currentY > doc.internal.pageSize.height - 30) {
        doc.addPage();
        currentY = 20;
        drawTableHeader();
      }

      const rowData = [
        index + 1,
        pegawai.nama_pegawai || "",
        pegawai.apel_pagi?.jam_absen || "Belum Absen",
        pegawai.apel_pagi?.status || "-",
        pegawai.apel_siang?.jam_absen || "Belum Absen",
        pegawai.apel_siang?.status || "-",
      ];

      drawTableRow(rowData);
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      doc.text(
        `Halaman ${i} dari ${pageCount}`,
        doc.internal.pageSize.width - 40,
        doc.internal.pageSize.height - 10
      );
      doc.text(
        `Dibuat pada: ${new Date().toLocaleString("id-ID")}`,
        20,
        doc.internal.pageSize.height - 10
      );
    }

    // Simpan PDF
    doc.save(`apel-harian-dinas-${selectedTanggal}.pdf`);
  };

  // Kolom untuk tabel data pegawai
  const columns = [
    { Header: "No", accessor: "index", width: "5%" },
    {
      Header: "Profil",
      accessor: "profil",
      width: "8%",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center">
          <img
            src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
            alt={row.original.nama_pegawai}
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid #e0e0e0",
            }}
            onError={(e) => {
              // Jika gambar tidak ditemukan, tampilkan icon default
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <MDBox
            display="none"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "grey.200",
              color: "grey.500",
            }}
          >
            <Icon sx={{ fontSize: 20 }}>person</Icon>
          </MDBox>
        </MDBox>
      ),
    },
    // Kolom aksi hanya untuk admin
    ...(userStatus === "admin"
      ? [
          {
            Header: "Aksi",
            accessor: "actions",
            width: "10%",
            Cell: ({ row }) => (
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={() => handleOpenDialog(row.original)}
              >
                Catat
              </MDButton>
            ),
          },
        ]
      : []),
    { Header: "Nama Pegawai", accessor: "nama_pegawai", width: "32%" },
    {
      Header: "Apel Pagi",
      accessor: "apel_pagi",
      width: "18%",
      Cell: ({ value, row }) => (
        <MDBox>
          <MDTypography
            variant="caption"
            color="text"
            display="block"
            sx={{ cursor: "pointer" }}
            onClick={() => handleOpenDetailModal(row.original)}
          >
            {value?.jam_absen || "Belum Absen"}
          </MDTypography>
          {value?.status && (
            <Chip
              label={value.status}
              color={value.status === "Hadir" ? "success" : "warning"}
              size="small"
              variant="filled"
              sx={{ mt: 0.5 }}
            />
          )}
        </MDBox>
      ),
    },
    {
      Header: "Apel Siang",
      accessor: "apel_siang",
      width: "18%",
      Cell: ({ value, row }) => (
        <MDBox>
          <MDTypography
            variant="caption"
            color="text"
            display="block"
            sx={{ cursor: "pointer" }}
            onClick={() => handleOpenDetailModal(row.original)}
          >
            {value?.jam_absen || "Belum Absen"}
          </MDTypography>
          {value?.status && (
            <Chip
              label={value.status}
              color={value.status === "Hadir" ? "success" : "warning"}
              size="small"
              variant="filled"
              sx={{ mt: 0.5 }}
            />
          )}
        </MDBox>
      ),
    },
  ];

  // Rows untuk tabel data pegawai
  const rows =
    dataApelDinas?.data_pegawai?.map((pegawai, index) => ({
      index: index + 1,
      uid_RSS: pegawai.uid_RSS,
      nama_pegawai: pegawai.nama_pegawai,
      apel_pagi: pegawai.apel_pagi,
      apel_siang: pegawai.apel_siang,
    })) || [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Grid container spacing={3}>
          {/* Statistik Card dengan Filter Tanggal */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={3}
                >
                  <MDBox>
                    <MDTypography variant="h6" gutterBottom>
                      Statistik Apel Harian
                    </MDTypography>
                    <MDBox display="flex" gap={1} mt={1}>
                      <MDButton
                        variant={
                          selectedApelType === "pagi" ? "contained" : "outlined"
                        }
                        color="info"
                        size="small"
                        onClick={() => setSelectedApelType("pagi")}
                      >
                        Apel Pagi
                      </MDButton>
                      <MDButton
                        variant={
                          selectedApelType === "siang"
                            ? "contained"
                            : "outlined"
                        }
                        color="info"
                        size="small"
                        onClick={() => setSelectedApelType("siang")}
                      >
                        Apel Siang
                      </MDButton>
                      <MDButton
                        variant={
                          selectedApelType === "gabungan"
                            ? "contained"
                            : "outlined"
                        }
                        color="info"
                        size="small"
                        onClick={() => setSelectedApelType("gabungan")}
                      >
                        Gabungan
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="success"
                        size="small"
                        onClick={fetchApelDinas}
                        disabled={loading}
                      >
                        <Icon>refresh</Icon>
                        &nbsp;Refresh
                      </MDButton>
                    </MDBox>
                  </MDBox>
                  <MDBox>
                    <MDInput
                      type="date"
                      label="Pilih Tanggal"
                      value={selectedTanggal}
                      onChange={handleTanggalChange}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </MDBox>
                </MDBox>

                {dataApelDinas && (
                  <>
                    <MDTypography variant="h6" color="text" mb={2}>
                      Rekap Apel Harian - {dataApelDinas.nama_dinas} -{" "}
                      {new Date(dataApelDinas.tanggal).toLocaleDateString(
                        "id-ID"
                      )}
                      (
                      {selectedApelType === "pagi"
                        ? "Apel Pagi"
                        : selectedApelType === "siang"
                        ? "Apel Siang"
                        : "Gabungan Pagi & Siang"}
                      )
                    </MDTypography>

                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        <Chip
                          icon={<Icon sx={{ color: "white" }}>group</Icon>}
                          label={`Total: ${dataApelDinas.total_pegawai}`}
                          variant="filled"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: "bold",
                            backgroundColor: "#424242",
                            color: "white",
                            "& .MuiChip-icon": {
                              color: "white",
                            },
                            "& .MuiChip-label": {
                              color: "white",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>check_circle</Icon>}
                          label={`Hadir: ${
                            selectedApelType === "pagi"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_pagi?.status === "Hadir"
                                ).length
                              : selectedApelType === "siang"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_siang?.status === "Hadir"
                                ).length
                              : dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_pagi?.status === "Hadir" ||
                                    p.apel_siang?.status === "Hadir"
                                ).length
                          }`}
                          color="success"
                          variant="filled"
                          sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>warning</Icon>}
                          label={`Izin: ${
                            selectedApelType === "pagi"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_pagi?.status === "Izin"
                                ).length
                              : selectedApelType === "siang"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_siang?.status === "Izin"
                                ).length
                              : dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_pagi?.status === "Izin" ||
                                    p.apel_siang?.status === "Izin"
                                ).length
                          }`}
                          color="warning"
                          variant="filled"
                          sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>local_hospital</Icon>}
                          label={`Sakit: ${
                            selectedApelType === "pagi"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_pagi?.status === "Sakit"
                                ).length
                              : selectedApelType === "siang"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_siang?.status === "Sakit"
                                ).length
                              : dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_pagi?.status === "Sakit" ||
                                    p.apel_siang?.status === "Sakit"
                                ).length
                          }`}
                          color="error"
                          variant="filled"
                          sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>work</Icon>}
                          label={`Dinas Luar: ${
                            selectedApelType === "pagi"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_pagi?.status === "Dinas Luar"
                                ).length
                              : selectedApelType === "siang"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_siang?.status === "Dinas Luar"
                                ).length
                              : dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_pagi?.status === "Dinas Luar" ||
                                    p.apel_siang?.status === "Dinas Luar"
                                ).length
                          }`}
                          variant="filled"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: "bold",
                            backgroundColor: "#9c27b0",
                            color: "white",
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={
                            <Icon sx={{ color: "white" }}>not_interested</Icon>
                          }
                          label={`Tanpa Berita: ${
                            selectedApelType === "pagi"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_pagi?.status === "Tanpa Berita"
                                ).length
                              : selectedApelType === "siang"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_siang?.status === "Tanpa Berita"
                                ).length
                              : dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_pagi?.status === "Tanpa Berita" ||
                                    p.apel_siang?.status === "Tanpa Berita"
                                ).length
                          }`}
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: "bold",
                            backgroundColor: "#424242",
                            color: "white",
                            "& .MuiChip-icon": {
                              color: "white",
                            },
                            "& .MuiChip-label": {
                              color: "white",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>event_busy</Icon>}
                          label={`Cuti: ${
                            selectedApelType === "pagi"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_pagi?.status === "Cuti"
                                ).length
                              : selectedApelType === "siang"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_siang?.status === "Cuti"
                                ).length
                              : dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_pagi?.status === "Cuti" ||
                                    p.apel_siang?.status === "Cuti"
                                ).length
                          }`}
                          color="info"
                          variant="filled"
                          sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>school</Icon>}
                          label={`Tugas Belajar: ${
                            selectedApelType === "pagi"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_pagi?.status === "Tugas Belajar"
                                ).length
                              : selectedApelType === "siang"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_siang?.status === "Tugas Belajar"
                                ).length
                              : dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_pagi?.status === "Tugas Belajar" ||
                                    p.apel_siang?.status === "Tugas Belajar"
                                ).length
                          }`}
                          color="primary"
                          variant="filled"
                          sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Tabel Data Pegawai */}
          {dataApelDinas && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <MDBox>
                      <MDTypography variant="h6" color="text">
                        Data Apel Pegawai:{" "}
                        {new Date(selectedTanggal).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Total: {dataApelDinas.data_pegawai.length} pegawai
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" gap={1}>
                      <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={handleExportExcel}
                        disabled={loading}
                      >
                        <Icon>download</Icon>
                        &nbsp;Export Excel
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="error"
                        onClick={handleExportPDF}
                        disabled={loading}
                      >
                        <Icon>picture_as_pdf</Icon>
                        &nbsp;Export PDF
                      </MDButton>
                    </MDBox>
                  </MDBox>
                  {isMobile ? (
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      {loading && (
                        <MDTypography variant="button" color="text.secondary">
                          Memuat...
                        </MDTypography>
                      )}
                      {!loading &&
                        rows.map((row, index) => (
                          <Card key={`${row.uid_RSS || "row"}-${index}`}>
                            <CardContent>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={4}>
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <img
                                      src={`https://smartdh.rotendaokab.go.id/uploads/${row.uid_RSS}.jpg`}
                                      alt={row.nama_pegawai}
                                      style={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                        border: "2px solid #e0e0e0",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display =
                                          "flex";
                                      }}
                                    />
                                    <MDBox
                                      display="none"
                                      alignItems="center"
                                      justifyContent="center"
                                      sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        backgroundColor: "grey.200",
                                        color: "grey.500",
                                      }}
                                    >
                                      <Icon sx={{ fontSize: 36 }}>person</Icon>
                                    </MDBox>
                                  </MDBox>
                                </Grid>
                                <Grid item xs={8}>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                  >
                                    {row.nama_pegawai}
                                  </MDTypography>
                                  <MDBox
                                    mt={1}
                                    display="flex"
                                    flexWrap="wrap"
                                    gap={0.5}
                                  >
                                    <Chip
                                      label={`Pagi: ${
                                        row.apel_pagi?.jam_absen ||
                                        "Belum Absen"
                                      }`}
                                      color={
                                        row.apel_pagi?.status === "Hadir"
                                          ? "success"
                                          : "warning"
                                      }
                                      size="small"
                                      onClick={() => handleOpenDetailModal(row)}
                                      sx={{ cursor: "pointer" }}
                                    />
                                    <Chip
                                      label={`Siang: ${
                                        row.apel_siang?.jam_absen ||
                                        "Belum Absen"
                                      }`}
                                      color={
                                        row.apel_siang?.status === "Hadir"
                                          ? "success"
                                          : "warning"
                                      }
                                      size="small"
                                      onClick={() => handleOpenDetailModal(row)}
                                      sx={{ cursor: "pointer" }}
                                    />
                                  </MDBox>
                                  <MDBox mt={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Status Pagi:{" "}
                                      {row.apel_pagi?.status || "Belum Absen"}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Status Siang:{" "}
                                      {row.apel_siang?.status || "Belum Absen"}
                                    </MDTypography>
                                  </MDBox>
                                  {userStatus === "admin" && (
                                    <MDBox mt={1}>
                                      <MDButton
                                        variant="outlined"
                                        color="info"
                                        size="small"
                                        onClick={() => handleOpenDialog(row)}
                                        sx={{ minWidth: "auto", px: 1 }}
                                      >
                                        <Icon sx={{ fontSize: 16 }}>edit</Icon>
                                        &nbsp;Catat
                                      </MDButton>
                                    </MDBox>
                                  )}
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        ))}
                    </MDBox>
                  ) : (
                    <DataTable
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                      loading={loading}
                    />
                  )}
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Loading State */}
          {loading && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDTypography variant="body1" color="text">
                    Memuat data apel dinas...
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Empty State */}
          {!dataApelDinas && !loading && userDinasId && selectedTanggal && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    py={4}
                  >
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
                      event_busy
                    </Icon>
                    <MDTypography variant="h6" color="text.secondary">
                      Tidak ada data apel harian
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      Coba pilih tanggal lain atau periksa kembali dinas
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Dialog Form Apel */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">
                {isEditMode ? "Edit" : "Tambah"} Data Apel -{" "}
                {selectedPegawai?.nama_pegawai}
              </MDTypography>
              <MDButton
                variant="outlined"
                color="dark"
                onClick={() => navigate(-1)}
              >
                <Icon>arrow_back</Icon>&nbsp;Kembali
              </MDButton>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Tanggal"
                    type="date"
                    value={form.tanggal}
                    onChange={(e) =>
                      setForm({ ...form, tanggal: e.target.value })
                    }
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nama Pegawai"
                    value={selectedPegawai?.nama_pegawai || ""}
                    variant="outlined"
                    disabled
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={jenisApelOptions}
                    value={form.jenis_apel}
                    onChange={(event, newValue) =>
                      setForm({ ...form, jenis_apel: newValue })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Jenis Apel"
                        variant="outlined"
                      />
                    )}
                    disabled={isEditMode}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Jam Absen"
                    type="time"
                    value={form.jam_absen}
                    onChange={(e) =>
                      setForm({ ...form, jam_absen: e.target.value })
                    }
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={statusOptions}
                    value={form.status}
                    onChange={(event, newValue) =>
                      setForm({ ...form, status: newValue })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Status"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Keterangan"
                    value={form.keterangan}
                    onChange={(e) =>
                      setForm({ ...form, keterangan: e.target.value })
                    }
                    variant="outlined"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseDialog}>
              Batal
            </MDButton>
            <MDButton color="info" variant="contained" onClick={handleSubmit}>
              {isEditMode ? "Update" : "Simpan"}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Modal Detail Apel */}
        <Dialog
          open={openDetailModal}
          onClose={handleCloseDetailModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">
                Detail Apel - {selectedApelDetail?.nama_pegawai}
              </MDTypography>
              <MDButton
                variant="outlined"
                color="dark"
                onClick={() => navigate(-1)}
              >
                <Icon>arrow_back</Icon>&nbsp;Kembali
              </MDButton>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              {selectedApelDetail && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <MDTypography variant="h6" color="info" gutterBottom>
                      Informasi Pegawai
                    </MDTypography>
                    <MDBox p={2} bgcolor="grey.50" borderRadius={1}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <MDTypography variant="subtitle2" color="text">
                            Nama: {selectedApelDetail.nama_pegawai}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={6}>
                          <MDTypography variant="subtitle2" color="text">
                            Jabatan: {selectedApelDetail.nama_jabatan}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={6}>
                          <MDTypography variant="subtitle2" color="text">
                            Pangkat: {selectedApelDetail.pangkat}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={6}>
                          <MDTypography variant="subtitle2" color="text">
                            Tanggal:{" "}
                            {new Date(selectedTanggal).toLocaleDateString(
                              "id-ID"
                            )}
                          </MDTypography>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDTypography variant="h6" color="success" gutterBottom>
                      Apel Pagi
                    </MDTypography>
                    <Card sx={{ p: 2 }}>
                      <MDBox>
                        <MDTypography
                          variant="subtitle2"
                          color="text"
                          gutterBottom
                        >
                          Status:{" "}
                          {selectedApelDetail.apel_pagi?.status ||
                            "Belum Absen"}
                        </MDTypography>
                        {selectedApelDetail.apel_pagi?.jam_absen && (
                          <MDTypography
                            variant="subtitle2"
                            color="text"
                            gutterBottom
                          >
                            Jam: {selectedApelDetail.apel_pagi.jam_absen}
                          </MDTypography>
                        )}
                        {selectedApelDetail.apel_pagi?.keterangan && (
                          <MDTypography
                            variant="subtitle2"
                            color="text"
                            gutterBottom
                          >
                            Keterangan:{" "}
                            {selectedApelDetail.apel_pagi.keterangan}
                          </MDTypography>
                        )}
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDTypography variant="h6" color="primary" gutterBottom>
                      Apel Siang
                    </MDTypography>
                    <Card sx={{ p: 2 }}>
                      <MDBox>
                        <MDTypography
                          variant="subtitle2"
                          color="text"
                          gutterBottom
                        >
                          Status:{" "}
                          {selectedApelDetail.apel_siang?.status ||
                            "Belum Absen"}
                        </MDTypography>
                        {selectedApelDetail.apel_siang?.jam_absen && (
                          <MDTypography
                            variant="subtitle2"
                            color="text"
                            gutterBottom
                          >
                            Jam: {selectedApelDetail.apel_siang.jam_absen}
                          </MDTypography>
                        )}
                        {selectedApelDetail.apel_siang?.keterangan && (
                          <MDTypography
                            variant="subtitle2"
                            color="text"
                            gutterBottom
                          >
                            Keterangan:{" "}
                            {selectedApelDetail.apel_siang.keterangan}
                          </MDTypography>
                        )}
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseDetailModal}>
              Tutup
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ApelDinas;
